import { useState } from 'react';

export interface DonutSlice {
  label: string;
  value: number;
}

export interface DonutChartProps {
  slices: DonutSlice[];
  /** What the number in the hole counts — "tickets", "alerts". */
  unit?: string;
  size?: number;
  emptyMessage?: string;
  /** Makes each slice clickable — for a drill-down into what it is made of. */
  onSelect?: (slice: DonutSlice) => void;
  /** The slice currently drilled into, drawn at full strength while the rest recede. */
  selected?: string | null;
}

/* The categorical order, fixed: a slice keeps its colour whatever else is on
   the chart. Six hues, so anything past the fifth folds into "Other". */
const COLORS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)', 'var(--series-4)', 'var(--series-5)', 'var(--series-6)'];
const MAX = COLORS.length;
const OTHER = 'Other';

/**
 * A donut — the part-to-whole of a handful of categories, with the total in
 * the hole.
 *
 * Kept to six slices: the five largest and "Other", because a seventh hue is
 * one a reader cannot tell from its neighbour. The legend carries every
 * slice's count and share as text, so the chart never depends on judging an
 * angle and stays readable in one colour. Colours go through `style`, never a
 * presentation attribute — see chartUtils.ts.
 */
export function DonutChart({ slices, unit = '', size = 200, emptyMessage = 'Nothing to show.', onSelect, selected = null }: DonutChartProps) {
  const [hover, setHover] = useState<number | null>(null);

  const sorted = [...slices].filter((s) => s.value > 0).sort((a, b) => b.value - a.value);
  if (!sorted.length) return <div className="empty">{emptyMessage}</div>;

  const shown: (DonutSlice & { other?: boolean })[] =
    sorted.length > MAX
      ? [...sorted.slice(0, MAX - 1), { label: OTHER, value: sorted.slice(MAX - 1).reduce((s, x) => s + x.value, 0), other: true }]
      : sorted;
  const total = shown.reduce((s, x) => s + x.value, 0);

  const r = size / 2;
  const outer = r - 4;
  const inner = outer * 0.62;
  let angle = -Math.PI / 2;
  const arcs = shown.map((s) => {
    const sweep = (s.value / total) * Math.PI * 2;
    const a0 = angle;
    angle += sweep;
    return { ...s, a0, a1: angle };
  });

  const point = (rad: number, a: number) => [r + rad * Math.cos(a), r + rad * Math.sin(a)] as const;
  const path = (a0: number, a1: number) => {
    /* A full circle as one arc has coincident end points and draws nothing. */
    if (a1 - a0 >= Math.PI * 2 - 1e-6) a1 = a0 + Math.PI * 2 - 1e-4;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const [x0, y0] = point(outer, a0);
    const [x1, y1] = point(outer, a1);
    const [x2, y2] = point(inner, a1);
    const [x3, y3] = point(inner, a0);
    return `M${x0},${y0} A${outer},${outer} 0 ${large} 1 ${x1},${y1} L${x2},${y2} A${inner},${inner} 0 ${large} 0 ${x3},${y3} Z`;
  };

  const pct = (v: number) => `${Math.round((v / total) * 100)}%`;
  const hovered = hover !== null ? arcs[hover] : null;
  const clickable = (s: { other?: boolean }) => !!onSelect && !s.other;
  const dim = (i: number, label: string) => (hover !== null ? hover !== i : selected !== null && selected !== label);

  return (
    <div className="donut">
      <div className="chart-frame donut-plot" style={{ width: size }}>
        <svg className="chart" width={size} height={size} role="img" aria-label={`Donut chart of ${total} ${unit}`} onMouseLeave={() => setHover(null)}>
          {arcs.map((a, i) => (
            <path
              key={a.label}
              d={path(a.a0, a.a1)}
              className="donut-slice"
              opacity={dim(i, a.label) ? 0.42 : 1}
              style={{ fill: COLORS[i], cursor: clickable(a) ? 'pointer' : undefined }}
              onMouseEnter={() => setHover(i)}
              onClick={clickable(a) ? () => onSelect!(a) : undefined}
            />
          ))}
          <text className="donut-total" x={r} y={r - 4} textAnchor="middle" dominantBaseline="middle">
            {hovered ? hovered.value.toLocaleString() : total.toLocaleString()}
          </text>
          <text className="chart-tick" x={r} y={r + 16} textAnchor="middle">
            {hovered ? pct(hovered.value) : unit}
          </text>
        </svg>
        {hovered && (
          /* Below the ring rather than above it, where it would cover the card's subtitle. */
          <div className="chart-tooltip" style={{ left: size / 2, top: size + 4 }}>
            <div className="chart-tt-row">
              <span className="chart-swatch" style={{ background: COLORS[hover!] }} />
              <span>{hovered.label}</span>
              <span className="chart-tt-value">
                {hovered.value.toLocaleString()} · {pct(hovered.value)}
              </span>
            </div>
          </div>
        )}
      </div>
      <ul className="donut-legend">
        {arcs.map((a, i) => (
          <li key={a.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            {clickable(a) ? (
              <button type="button" className={`donut-key${selected === a.label ? ' on' : ''}`} onClick={() => onSelect!(a)}>
                <span className="chart-legend-swatch" style={{ background: COLORS[i] }} />
                <span className="donut-key-label" title={a.label}>{a.label}</span>
                <span className="donut-key-value">{a.value.toLocaleString()}</span>
                <span className="donut-key-pct">{pct(a.value)}</span>
              </button>
            ) : (
              <span className="donut-key">
                <span className="chart-legend-swatch" style={{ background: COLORS[i] }} />
                <span className="donut-key-label" title={a.label}>
                  {a.label}
                  {a.other && <span className="muted"> ({sorted.length - (MAX - 1)})</span>}
                </span>
                <span className="donut-key-value">{a.value.toLocaleString()}</span>
                <span className="donut-key-pct">{pct(a.value)}</span>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
